import s from './Header.module.scss'

import React from 'react'
import { ReactComponent as BinanceLogo } from 'shared/img/binance-icon.svg'
import { ReactComponent as BybitLogo } from 'shared/img/bybit-icon.svg'
import { ReactComponent as BitgetLogo } from 'shared/img/bitget-icon.svg'
import { ReactComponent as OkxLogo } from 'shared/img/okx-icon.svg'
import RithmicLogo from 'shared/img/rithmic-icon.png'
import { ItemType } from 'antd/lib/menu/hooks/useItems'
import { Link } from 'react-router-dom'
import { NAV_LINK_NAMES } from 'shared/consts'

import menuTranslations, { MenuTranslations } from './translations'

/**
 * Элемент ссылки для меню
 * @param label название
 * @param link ссылка
 */
export const getMenuLink = (label: string, link: string) => (
    <Link to={link}>{label}</Link>
)

/**
 * Главное меню (для всех залогиненных пользователей).
 */
export const getMenuItem = (t: MenuTranslations): ItemType[] => [
    {
        label: t.trade,
        key: 'trade',
        children: [
            {
                label: t.crypto,
                key: 'crypto',
                children: [
                    {
                        label: getMenuLink(
                            'Binance',
                            NAV_LINK_NAMES.TRADING_BINANCE
                        ),
                        key: 'robotBinance',
                        icon: <BinanceLogo className={s.itemLogo} />,
                    },
                    {
                        label: getMenuLink(
                            'ByBit',
                            NAV_LINK_NAMES.TRADING_BYBIT
                        ),
                        key: 'robotBybit',
                        icon: <BybitLogo className={s.itemLogo} />,
                    },
                    {
                        label: getMenuLink(
                            'Bitget',
                            NAV_LINK_NAMES.TRADING_BITGET
                        ),
                        key: 'robotBitget',
                        icon: <BitgetLogo className={s.itemLogo} />,
                    },
                    {
                        label: getMenuLink(
                            'OKX',
                            NAV_LINK_NAMES.TRADING_OKX
                        ),
                        key: 'robotOkx',
                        icon: <OkxLogo className={s.itemLogo} />,
                    },
                ],
            },
            {
                label: t.global,
                key: 'global',
                children: [
                    {
                        label: getMenuLink(
                            'Rithmic',
                            NAV_LINK_NAMES.TRADING_RITHMIC
                        ),
                        key: 'robotRithmic',
                        icon: (
                            <img
                                src={RithmicLogo}
                                className={s.itemLogo}
                                alt="Rithmic"
                            />
                        ),
                    },
                ],
            },
        ],
    },
]

/**
 * Dropdown профиля в Header — заголовок (аватар + имя) ведёт на /account,
 * внутри: Account, Staking, Referral, Partner.
 */
export const getProfileMenuItem = (
    t: MenuTranslations,
    label: React.ReactNode
): ItemType[] => [
    {
        label,
        key: 'profile',
        children: [
            {
                label: getMenuLink(t.account, NAV_LINK_NAMES.ACCOUNT),
                key: 'account',
            },
            {
                label: getMenuLink(t.staking, NAV_LINK_NAMES.STAKING),
                key: 'staking',
            },
            {
                label: getMenuLink(t.referral, NAV_LINK_NAMES.REFERRAL),
                key: 'referral',
            },
            {
                label: getMenuLink(t.partner, NAV_LINK_NAMES.PARTNER),
                key: 'partner',
            },
        ],
    },
]

/**
 * Партнёрская «админка» (только is_partner = true).
 *
 * Дропдаун «Партнёр» как у админа: по биржам, но внутри только
 * «Клиенты» (read-only торговля своих клиентов).  «Символы» — серой
 * неактивной кнопкой (партнёру недоступны, но видно что они есть).
 */
export const getPartnerMenuItem = (t: MenuTranslations): ItemType[] => {
    const exchange = (
        name: string,
        key: string,
        clientsLink: string
    ): ItemType => ({
        label: name,
        key,
        children: [
            {
                label: getMenuLink(t.clients, clientsLink),
                key: `${key}Clients`,
            },
            {
                // Символы недоступны партнёру — серый неактивный пункт.
                label: t.symbols,
                key: `${key}Symbols`,
                disabled: true,
            },
        ],
    })

    return [
        {
            label: t.partnerPanel,
            key: 'partnerPanel',
            children: [
                exchange(
                    'Binance',
                    'partnerBinance',
                    NAV_LINK_NAMES.PARTNER_CLIENTS_BINANCE
                ),
                exchange(
                    'ByBit',
                    'partnerBybit',
                    NAV_LINK_NAMES.PARTNER_CLIENTS_BYBIT
                ),
                exchange(
                    'OKX',
                    'partnerOkx',
                    NAV_LINK_NAMES.PARTNER_CLIENTS_OKX
                ),
                exchange(
                    'Rithmic',
                    'partnerRithmic',
                    NAV_LINK_NAMES.PARTNER_CLIENTS_RITHMIC
                ),
            ],
        },
    ]
}

/**
 * Админ-меню (только для is_admin = true).
 *
 * Stage 2026-05-30 — сжато в ОДИН dropdown «Admin» чтобы не засорять
 * top-level header.  Раньше 5 отдельных пунктов (Binance, ByBit, OKX,
 * FCM, Заявки партнёров) занимали 5 слотов в шапке.  Теперь — один.
 */
export const getAdminMenuItem = (t: MenuTranslations): ItemType[] => [
    {
        label: t.adminMenu,
        key: 'adminMenu',
        children: [
            {
                label: 'Binance',
                key: 'binance',
                children: [
                    {
                        label: getMenuLink(
                            t.accounts,
                            NAV_LINK_NAMES.BINANCE_ACCOUNTS
                        ),
                        key: 'binanceAccounts',
                    },
                    {
                        label: getMenuLink(
                            t.symbols,
                            NAV_LINK_NAMES.BINANCE_SYMBOLS
                        ),
                        key: 'binanceSymbols',
                    },
                ],
            },
            {
                label: 'ByBit',
                key: 'bybit',
                children: [
                    {
                        label: getMenuLink(
                            t.accounts,
                            NAV_LINK_NAMES.BYBIT_ACCOUNTS
                        ),
                        key: 'bybitAccounts',
                    },
                    {
                        label: getMenuLink(
                            t.symbols,
                            NAV_LINK_NAMES.BYBIT_SYMBOLS
                        ),
                        key: 'bybitSymbols',
                    },
                ],
            },
            {
                label: 'Bitget',
                key: 'bitget',
                children: [
                    {
                        label: getMenuLink(
                            t.accounts,
                            NAV_LINK_NAMES.BITGET_ACCOUNTS
                        ),
                        key: 'bitgetAccounts',
                    },
                    {
                        label: getMenuLink(
                            t.symbols,
                            NAV_LINK_NAMES.BITGET_SYMBOLS
                        ),
                        key: 'bitgetSymbols',
                    },
                ],
            },
            {
                label: 'OKX',
                key: 'okx',
                children: [
                    {
                        label: getMenuLink(
                            t.accounts,
                            NAV_LINK_NAMES.OKX_ACCOUNTS
                        ),
                        key: 'okxAccounts',
                    },
                    {
                        label: getMenuLink(
                            t.symbols,
                            NAV_LINK_NAMES.OKX_SYMBOLS
                        ),
                        key: 'okxSymbols',
                    },
                ],
            },
            {
                label: t.fcm,
                key: 'FCM',
                children: [
                    {
                        label: 'Rithmic',
                        key: 'advanced',
                        children: [
                            {
                                label: getMenuLink(
                                    t.accounts,
                                    NAV_LINK_NAMES.RITHMIC_ACCOUNTS
                                ),
                                key: 'rithmicAccounts',
                            },
                            {
                                label: getMenuLink(
                                    t.symbols,
                                    NAV_LINK_NAMES.RITHMIC_SYMBOLS
                                ),
                                key: 'rithmicSymbols',
                            },
                        ],
                    },
                ],
            },
            {
                label: getMenuLink(
                    t.partnerRequests,
                    NAV_LINK_NAMES.PARTNER_REQUESTS
                ),
                key: 'partnerRequests',
            },
            {
                // Заявки клиентов на свой инструмент (shared/symbol_proposals.py).
                label: getMenuLink(
                    'Инструменты клиентов',
                    NAV_LINK_NAMES.SYMBOL_PROPOSALS
                ),
                key: 'symbolProposals',
            },
            {
                // Презентация архитектуры единого ядра (только для админа).
                label: getMenuLink(
                    '⬢ Архитектура ядра',
                    NAV_LINK_NAMES.ARCHITECTURE
                ),
                key: 'architecture',
            },
            {
                // Книга — очищенный нарратив проекта (только для админа).
                label: getMenuLink('📖 Книга', NAV_LINK_NAMES.BOOK),
                key: 'book',
            },
        ],
    },
]

// Backwards-compat: на случай если где-то ещё импортируют константу
// напрямую.  Использует английский fallback.  Все новые вызовы — через
// функции getMenuItem(t) / getAdminMenuItem(t).
export const MENU_ITEM: ItemType[] = getMenuItem(menuTranslations.en)
export const ADMIN_MENU_ITEM: ItemType[] = getAdminMenuItem(menuTranslations.en)
